import React, { useState, useRef, useEffect, forwardRef, MutableRefObject, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useRole, useClick, useInteractions } from '@floating-ui/react';

import { logger } from '@/utils/logger';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DropdownProps {
  value: string;
  onChange: (value: string | undefined) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  tooltip?: string;
  debug?: boolean;
  testId?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(function Dropdown({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  error = false,
  tooltip = '',
  debug = false,
  testId = 'dropdown',
  onFocus,
  onBlur,
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  let blurTimeout: ReturnType<typeof setTimeout> | null = null;

  // Floating UI setup for proper positioning
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    strategy: 'fixed', // Fixed positioning to handle scroll containers properly
    middleware: [
      offset(4), // Small gap between button and dropdown
      flip({
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
        crossAxis: true
      }),
      shift({ padding: 8 }) // Keep dropdown within viewport
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getFloatingProps, getReferenceProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // Attach forwarded ref to button and sync with floating-ui
  useEffect(() => {
    if (typeof ref === 'function') {
      ref(buttonRef.current);
    } else if (ref) {
      (ref as MutableRefObject<HTMLButtonElement | null>).current = buttonRef.current;
    }
    // Sync buttonRef with floating-ui reference
    if (buttonRef.current) {
      refs.setReference(buttonRef.current);
    }
  }, [ref, refs]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  // Debug logging
  const debugLog = useCallback((...args: unknown[]) => {
    if (debug) logger.debug('[Dropdown]', ...args);
  }, [debug]);

  // Floating-ui handles click outside and positioning automatically

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        if (isOpen) {
          if (highlightedIndex >= 0) {
            const option = options[highlightedIndex];
            if (!option.disabled) {
              onChange(option.value);
              debugLog('Selected option', option.value);
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }
        } else {
          setIsOpen(true);
          setHighlightedIndex(0);
          debugLog('Opened by Enter/Space');
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        debugLog('Closed by Escape');
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (isOpen) {
          const nextIndex = Math.min(highlightedIndex + 1, options.length - 1);
          setHighlightedIndex(nextIndex);
        } else {
          setIsOpen(true);
          setHighlightedIndex(0);
          debugLog('Opened by ArrowDown');
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const prevIndex = Math.max(highlightedIndex - 1, 0);
          setHighlightedIndex(prevIndex);
        }
        break;
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleOptionClick = (e: React.MouseEvent, option: DropdownOption) => {
    // Prevent blur from firing before selection registers
    e.preventDefault();
    e.stopPropagation();
    
    if (!option.disabled) {
      // Clear any pending blur timeout
      if (blurTimeout) {
        clearTimeout(blurTimeout);
        blurTimeout = null;
      }
      onChange(option.value);
      debugLog('Selected option (click)', option.value);
      setIsOpen(false);
      setHighlightedIndex(-1);
      
      // Return focus to button after selection
      buttonRef.current?.focus();
    }
  };

  const handleButtonClick = () => {
    if (!disabled) {
      // Floating-ui handles open/close via useClick
      if (!isOpen) {
        setHighlightedIndex(0);
        debugLog('Opened by click');
      } else {
        debugLog('Closed by click');
      }
    }
  };

  // Delayed blur close
  const handleBlur = () => {
    blurTimeout = setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
      debugLog('Closed by blur');
      onBlur?.();
    }, 200); // 200ms delay - gives more time for mouseDown to register
  };
  const handleFocus = () => {
    if (blurTimeout) clearTimeout(blurTimeout);
    onFocus?.();
    debugLog('Focused');
  };

  // Dropdown menu rendered in portal with floating-ui positioning
  const dropdownMenu = isOpen ? createPortal(
    <div
      ref={(el) => {
        dropdownRef.current = el;
        refs.setFloating(el);
      }}
      className="z-[9999] rounded-md max-h-60 overflow-auto"
      style={{
        ...floatingStyles,
        width: buttonRef.current ? `${buttonRef.current.offsetWidth}px` : 'auto',
        backgroundColor: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-strong)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        opacity: 1,
        isolation: 'isolate'
      }}
      {...getFloatingProps()}
      role="listbox"
      aria-activedescendant={highlightedIndex >= 0 ? `dropdown-option-${highlightedIndex}` : undefined}
      data-testid={`${testId}-menu`}
    >
      {options.map((option, index) => (
        <div
          key={option.value}
          id={`dropdown-option-${index}`}
          role="option"
          aria-selected={value === option.value}
          className={`px-3 py-2 text-sm cursor-pointer text-textPrimary ${
            index === highlightedIndex ? 'bg-tableHover font-semibold' : 'hover:bg-tableHover'
          } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onMouseDown={(e) => handleOptionClick(e, option)}
          onClick={(e) => {
            // Backup click handler in case mouseDown doesn't fire
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseEnter={() => setHighlightedIndex(index)}
        >
          {option.label}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`relative ${className}` + (error ? ' border border-danger' : '')}>
      <button
        ref={(el) => {
          buttonRef.current = el;
          if (el) refs.setReference(el);
        }}
        type="button"
        className={`input-surface border-b border-tableBorder rounded-lg px-3 py-2 text-left flex items-center justify-between w-full ${
          disabled ? 'bg-disabledBg text-disabledText cursor-not-allowed' : 'cursor-pointer'
        } ${!selectedOption ? 'text-textMuted' : 'text-textPrimary'} focus:border-[var(--focus-ring)] focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] text-sm`}
        style={{
          backgroundColor: 'var(--bg-elev)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          isolation: 'isolate'
        }}
        {...getReferenceProps({
          onClick: handleButtonClick,
          onKeyDown: handleKeyDown,
          onFocus: handleFocus,
          onBlur: handleBlur,
        })}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${testId}-menu`}
        data-testid={`${testId}-button`}
        title={tooltip || 'Choose a campaign to attribute this spend'}
      >
        <span>{displayValue}</span>
        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {dropdownMenu}
    </div>
  );
});

export default Dropdown; 